import { useQuery } from "@tanstack/react-query";
import { Badge, Card, CardBody, Page, Table, type TableColumn } from "@theg1group/ui";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { BarChart } from "../components/BarChart.tsx";
import { KpiTile } from "../components/KpiTile.tsx";
import { PartnerStatusBadge } from "../components/PartnerStatusBadge.tsx";
import { RiskBadge } from "../components/RiskBadge.tsx";
import { api } from "../lib/api.ts";
import { fmtAgo, fmtDuration, fmtNum, fmtUsd, severityTone } from "../lib/format.ts";
import type { AlertRow, PartnerRow, Rows, Stats } from "../lib/types.ts";

export function Overview() {
	const stats = useQuery({
		queryKey: ["stats"],
		queryFn: () => api.get<Stats>("/api/stats"),
		refetchInterval: 30_000,
	});
	const alerts = useQuery({
		queryKey: ["alerts", { unread: 1, limit: 8 }],
		queryFn: () => api.get<Rows<AlertRow>>("/api/alerts?unread=1&limit=8"),
		refetchInterval: 30_000,
	});
	const partners = useQuery({
		queryKey: ["partners", "", ""],
		queryFn: () => api.get<Rows<PartnerRow>>("/api/partners?q=&status="),
		refetchInterval: 60_000,
	});
	const topRisk = [...(partners.data?.rows ?? [])]
		.sort((a, b) => (b.riskScore ?? 0) - (a.riskScore ?? 0))
		.filter((r) => (r.riskScore ?? 0) > 0)
		.slice(0, 5);

	const s = stats.data;
	const c = s?.counts;
	const j = s?.jobs;
	const w = s?.watch;

	const providerCols: TableColumn<Stats["providerCalls"][number]>[] = [
		{ id: "provider", header: "Provider", accessor: (r) => r.provider },
		{ id: "day", header: "Day", accessor: (r) => r.day },
		{ id: "calls", header: "Calls", accessor: (r) => fmtNum(r.calls) },
		{
			id: "errors",
			header: "Errors",
			accessor: (r) => (r.errors ? <span className="text-danger">{fmtNum(r.errors)}</span> : "0"),
		},
	];

	return (
		<Page
			title="Overview"
			description="Partner wallet coverage, ingestion backlog and watch health."
		>
			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}
			>
				<KpiTile
					label="Partners"
					value={fmtNum(c?.partners)}
					hint={c ? `${c.activePartners} active` : undefined}
				/>
				<KpiTile label="Wallets" value={fmtNum(c?.wallets)} />
				<KpiTile label="Addresses" value={fmtNum(c?.addresses)} />
				<KpiTile label="Transfers" value={fmtNum(c?.transfers)} />
				<KpiTile label="Labels" value={fmtNum(c?.labels)} />
				<KpiTile
					label="Unread alerts"
					value={fmtNum(c?.unreadAlerts)}
					tone={c?.unreadAlerts ? "danger" : "default"}
					hint={c ? `${c.alerts} total` : undefined}
				/>
			</div>

			<div
				className="grid gap-3.5 mb-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }}
			>
				<Panel title="Transfers per day">
					<BarChart
						data={(s?.transfersPerDay ?? []).map((d) => ({
							label: d.day,
							value: d.count,
							title: `${d.day}: ${d.count} transfers · ${fmtUsd(d.usd)}`,
						}))}
					/>
				</Panel>
				<Panel title="Trace jobs">
					<div className="grid grid-cols-2 gap-2">
						<Mini label="Queued" value={j?.queued} />
						<Mini label="Running" value={j?.running} />
						<Mini label="Done 24h" value={j?.done24h} />
						<Mini label="Errors 24h" value={j?.error24h} danger={!!j?.error24h} />
						<Mini label="Frontier pending" value={j?.pendingFrontier} />
						<Mini label="Frontier fetching" value={j?.fetchingFrontier} />
					</div>
					<Link to="/traces" className="text-primary text-xs hover:underline mt-2 inline-block">
						All traces →
					</Link>
				</Panel>
				<Panel title="Watch health">
					<div className="grid grid-cols-2 gap-2">
						<Mini label="Watched" value={w?.enabled} />
						<Mini label="Due" value={w?.due} />
						<Mini
							label="Lag"
							value={fmtDuration(w?.lagSeconds)}
							danger={(w?.lagSeconds ?? 0) > 3600}
						/>
						<Mini label="Errors" value={w?.errors} danger={!!w?.errors} />
					</div>
				</Panel>
				<Panel title="Chains">
					{(s?.chains ?? []).map((ch) => (
						<Row
							key={ch.chain}
							label={`${ch.chain} · ${fmtNum(ch.addresses)} addr`}
							value={ch.transfers}
							max={Math.max(...(s?.chains ?? []).map((x) => x.transfers), 1)}
						/>
					))}
					{!s?.chains?.length && <div className="text-xs text-muted">—</div>}
				</Panel>
			</div>

			<div
				className="grid gap-3.5"
				style={{ gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))" }}
			>
				<Card>
					<CardBody>
						<div className="flex items-center justify-between mb-3">
							<h2 className="text-sm font-semibold text-content">Top risk partners</h2>
							<Link to="/partners" className="text-primary text-xs hover:underline">
								All partners →
							</Link>
						</div>
						{topRisk.map((r) => (
							<div key={r.id} className="flex items-center gap-2 text-sm mb-2">
								<RiskBadge score={r.riskScore ?? 0} />
								<Link
									to={`/partners/${r.id}`}
									className="text-primary hover:underline truncate flex-1 min-w-0"
								>
									{r.name}
								</Link>
								<PartnerStatusBadge status={r.status} />
							</div>
						))}
						{!topRisk.length && (
							<div className="text-xs text-muted">
								{partners.isLoading ? "Loading…" : "No scored partners yet."}
							</div>
						)}
					</CardBody>
				</Card>
				<Card>
					<CardBody>
						<h2 className="text-sm font-semibold text-content mb-3">Provider calls</h2>
						<div className="overflow-x-auto">
							<Table
								columns={providerCols}
								data={s?.providerCalls ?? []}
								keyExtractor={(r) => `${r.provider}:${r.day}`}
								loading={stats.isLoading}
								emptyMessage="No provider calls yet."
							/>
						</div>
					</CardBody>
				</Card>
				<Card>
					<CardBody>
						<div className="flex items-center justify-between mb-3">
							<h2 className="text-sm font-semibold text-content">Latest unread alerts</h2>
							<Link to="/alerts" className="text-primary text-xs hover:underline">
								All alerts →
							</Link>
						</div>
						{(alerts.data?.rows ?? []).map((a) => (
							<div key={a.id} className="flex items-start gap-2 text-sm mb-2">
								<Badge variant={severityTone(a.severity)}>{a.severity}</Badge>
								<div className="min-w-0 flex-1">
									<div className="text-content truncate">{a.title}</div>
									<div className="text-[11px] text-muted">
										{a.kind}
										{a.partnerName && (
											<>
												{" · "}
												<Link
													to={`/partners/${a.partnerId}`}
													className="text-primary hover:underline"
												>
													{a.partnerName}
												</Link>
											</>
										)}
										{" · "}
										{fmtAgo(a.createdAt)}
									</div>
								</div>
							</div>
						))}
						{!alerts.data?.rows.length && (
							<div className="text-xs text-muted">No unread alerts.</div>
						)}
					</CardBody>
				</Card>
			</div>
		</Page>
	);
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
	return (
		<Card>
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-3">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}

function Mini({
	label,
	value,
	danger,
}: {
	label: string;
	value?: number | string | null;
	danger?: boolean;
}) {
	return (
		<div className="rounded-md bg-surface-2 px-2.5 py-1.5">
			<div className="text-[10px] uppercase text-muted">{label}</div>
			<div className={`text-base font-semibold ${danger ? "text-danger" : "text-content"}`}>
				{value === undefined || value === null
					? "—"
					: typeof value === "number"
						? fmtNum(value)
						: value}
			</div>
		</div>
	);
}

function Row({ label, value, max }: { label: string; value: number; max: number }) {
	return (
		<div className="mb-1.5">
			<div className="flex justify-between text-xs text-content mb-0.5">
				<span className="truncate mr-2">{label}</span>
				<span className="text-muted">{fmtNum(value)}</span>
			</div>
			<div className="h-1.5 rounded-pill bg-surface-2 overflow-hidden">
				<div className="h-full bg-primary" style={{ width: `${(value / max) * 100}%` }} />
			</div>
		</div>
	);
}
